export const HEADER_MENU_ENDPOINT = '/config/img-cloud-header-menu';

export const INTERNAL_HEADER_MENU_KEYS = [
  'home',
  'web-hosting',
  'galleries',
  'image-processing',
  'image-search',
  'ai-generate',
  'encrypted-links',
  'directories',
  'international',
  'image-categories',
  'faq',
  'about',
] as const;

export type InternalHeaderMenuKey = typeof INTERNAL_HEADER_MENU_KEYS[number];
export type HeaderMenuPlacement = 'primary' | 'more';

export interface InternalHeaderMenuItem {
  id: string;
  kind: 'internal';
  placement: HeaderMenuPlacement;
  menuKey: InternalHeaderMenuKey;
}

export interface ExternalHeaderMenuItem {
  id: string;
  kind: 'external';
  placement: HeaderMenuPlacement;
  labelZh: string;
  labelEn: string;
  url: string;
}

export type HeaderMenuItem = InternalHeaderMenuItem | ExternalHeaderMenuItem;

export const DEFAULT_HEADER_MENU_ITEMS: readonly HeaderMenuItem[] = [
  { id: 'home', kind: 'internal', placement: 'primary', menuKey: 'home' },
  { id: 'web-hosting', kind: 'internal', placement: 'primary', menuKey: 'web-hosting' },
  { id: 'galleries', kind: 'internal', placement: 'primary', menuKey: 'galleries' },
  { id: 'image-processing', kind: 'internal', placement: 'primary', menuKey: 'image-processing' },
  { id: 'image-search', kind: 'internal', placement: 'more', menuKey: 'image-search' },
  { id: 'ai-generate', kind: 'internal', placement: 'more', menuKey: 'ai-generate' },
  {
    id: 'html-share-collection',
    kind: 'external',
    placement: 'more',
    labelZh: '网页广场',
    labelEn: 'Web Plaza',
    url: '/html-share',
  },
  { id: 'encrypted-links', kind: 'internal', placement: 'more', menuKey: 'encrypted-links' },
  { id: 'directories', kind: 'internal', placement: 'more', menuKey: 'directories' },
  { id: 'international', kind: 'internal', placement: 'more', menuKey: 'international' },
  { id: 'image-categories', kind: 'internal', placement: 'more', menuKey: 'image-categories' },
  {
    id: 'blog',
    kind: 'external',
    placement: 'more',
    labelZh: '博客',
    labelEn: 'Blog',
    url: 'https://blog.imgbed.cn/',
  },
  { id: 'faq', kind: 'internal', placement: 'more', menuKey: 'faq' },
  { id: 'about', kind: 'internal', placement: 'more', menuKey: 'about' },
];

const INTERNAL_KEY_SET = new Set<string>(INTERNAL_HEADER_MENU_KEYS);
const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isSafeUrl = (url: string): boolean => {
  const hasControlCharacter = Array.from(url).some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });
  if (!url || url.length > 2048 || /\s/.test(url) || hasControlCharacter || url.includes('\\')) {
    return false;
  }
  if (url.startsWith('/')) {
    return url.length > 1 && !url.startsWith('//');
  }
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol)
      && Boolean(parsed.hostname)
      && !parsed.username
      && !parsed.password;
  } catch {
    return false;
  }
};

export const normalizeHeaderMenuItems = (value: unknown): HeaderMenuItem[] | null => {
  if (!Array.isArray(value) || value.length > 50) return null;
  const ids = new Set<string>();
  const internalKeys = new Set<string>();
  const items: HeaderMenuItem[] = [];

  for (const rawItem of value) {
    if (!isRecord(rawItem)) return null;
    const id = typeof rawItem.id === 'string' ? rawItem.id.trim() : '';
    if (!ID_PATTERN.test(id) || ids.has(id)) return null;
    ids.add(id);
    const placement = rawItem.placement;
    if (placement !== 'primary' && placement !== 'more') return null;

    if (rawItem.kind === 'internal') {
      const menuKey = typeof rawItem.menuKey === 'string' ? rawItem.menuKey : '';
      if (!INTERNAL_KEY_SET.has(menuKey) || internalKeys.has(menuKey) || id !== menuKey) return null;
      internalKeys.add(menuKey);
      items.push({
        id,
        kind: 'internal',
        placement,
        menuKey: menuKey as InternalHeaderMenuKey,
      });
      continue;
    }
    if (rawItem.kind !== 'external') return null;
    const labelZh = typeof rawItem.labelZh === 'string' ? rawItem.labelZh.trim() : '';
    const labelEn = typeof rawItem.labelEn === 'string' ? rawItem.labelEn.trim() : '';
    const url = typeof rawItem.url === 'string' ? rawItem.url.trim() : '';
    if (!labelZh || !labelEn || labelZh.length > 40 || labelEn.length > 40 || !isSafeUrl(url)) {
      return null;
    }
    items.push({ id, kind: 'external', placement, labelZh, labelEn, url });
  }
  return items;
};

export const loadHeaderMenu = async (): Promise<HeaderMenuItem[]> => {
  try {
    const response = await fetch(HEADER_MENU_ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = await response.json();
    if (body?.success === false) throw new Error(body?.message || 'Header menu request failed');
    const items = normalizeHeaderMenuItems(body?.data?.items);
    if (items === null) throw new Error('Invalid Header menu response');
    return items;
  } catch (error) {
    console.error('Failed to load Header menu, using defaults', error);
    return [...DEFAULT_HEADER_MENU_ITEMS];
  }
};
